import React, { useState, useRef, useEffect } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button, ButtonProps } from '../Button';
import { CloseOutlineIcon } from '@applyboard/ui-icons';
import { useFullTheme } from '~/design';
import { breakpoints } from '~/design/breakpoint';
import { hasSymbol, useResponsive } from '~/utils';
import { InternalText } from '~/internal/InternalText';
import { ScrollContainerProps, useOnScrollCallback } from '~/internal';

export type DialogSize = 'md' | 'lg' | 'xl' | 'full-width' | 'full-screen';
type ScrollPosition = 'top' | 'middle' | 'bottom' | 'none';

const symbolMap = {
  Heading: Symbol('Dialog.Heading'),
  Actions: Symbol('Dialog.Actions'),
  Content: Symbol('Dialog.Content'),
  Trigger: Symbol('Dialog.Trigger'),
} as const;

/**
 * Dialog context scope to get and update information between child components
 */
interface DialogScopeContext {
  /**
   * Context method that is instantiated with the Dialog.onClosing property.  It is used to send the signal back to the consumer that the Dialog is attempting to close.
   */
  onOpenChange: (open: boolean) => void;
  /**
   * Scrolling position can be "top", "middle", or "bottom"
   */
  scrollPosition: ScrollPosition;
  /**
   * Allows Dialog.Content to set the scrolling position state
   */
  setScrollPosition: (scrollPosition: ScrollPosition) => void;
  /**
   * Used by the Dialog's child components to receive the size property set in the root Dialog
   */
  size: DialogSize;
  /**
   * Used to check if the dialog is open
   */
  isOpen: boolean;
  /**
   * Wether or not to use a portal
   */
  portal: boolean;
}

const DialogContext = React.createContext<DialogScopeContext | null>(null);

/**
 * DialogProvider component to ensure that the DialogContext is scoped to this Dialog component
 */
interface DialogProviderProps {
  onOpenChange: (open: boolean) => void;
  children: React.ReactElement<DialogProps>;
  size: DialogSize;
  isOpen: boolean;
  portal: boolean;
}

function DialogProvider({ onOpenChange, children, size, isOpen, portal }: DialogProviderProps) {
  const [scrollPosition, setScrollPosition] = useState<ScrollPosition>('none');

  return (
    <DialogContext.Provider
      value={{
        onOpenChange,
        scrollPosition,
        setScrollPosition,
        size,
        isOpen,
        portal,
      }}
    >
      {children}
    </DialogContext.Provider>
  );
}

/**
 *
 * @returns Internal hook to get the DialogContext
 */
function useDialogContext() {
  const context = React.useContext(DialogContext);
  return context;
}

/**
 * Dialog component's properties.
 */
export interface DialogProps {
  /** Children should only be Heading, Content, and or Actions */
  children: React.ReactNode;

  /** Callback function that is invoked when the dialog is opened or closed. */
  onOpenChange: (open: boolean) => void;

  /** If true, the dialog will be ppen */
  open?: boolean;

  /** Size of the dialog. (default: md) */
  size?: DialogSize;
}

/**
 * A dialog is an overlay that requires users interaction and are designed to elicit a response from the users.
 * Dialogs are meant to give users critical information, makes users take important decision or involve multiple task it can also meant to provide users information regarding user process which are not critical.
 *
 * Note: Most `Dialog.X` components need to be rendered as direct children of `Dialog`, including `Dialog.Heading`, `Dialog.Actions`, `Dialog.Content`, and `Dialog.Trigger`
 *
 * ## ⚠️ Deprecation Warnings ⚠️
 * - `Dialog.Panel` is deprecated and no longer necessary.  Using `Dialog.Panel` will also cause the dialog to be rendered inline instead of via a react portal.  Rendering in a react portal is necessary to fix some visual/layout bugs.  If you're wondering how this impacts wrapping a dialog in a form, please see the form example for the recommended way to integrate a form with a dialog
 */

export function Dialog({ children, open = false, size = 'md', onOpenChange }: DialogProps) {
  const DialogRoot = DialogPrimitive.Root;
  const headingChild = React.Children.toArray(children).find(child =>
    hasSymbol(child, symbolMap.Heading),
  );
  const actionsChild = React.Children.toArray(children).find(child =>
    hasSymbol(child, symbolMap.Actions),
  );
  const contentChild = React.Children.toArray(children).find(child =>
    hasSymbol(child, symbolMap.Content),
  );
  const triggerChild = React.Children.toArray(children).find(child =>
    hasSymbol(child, symbolMap.Trigger),
  );
  const shouldUseSlots = Boolean(headingChild || actionsChild || contentChild);

  return (
    <DialogProvider onOpenChange={onOpenChange} size={size} isOpen={open} portal={shouldUseSlots}>
      <DialogRoot open={open} modal onOpenChange={onOpenChange}>
        {!shouldUseSlots && children}
        {shouldUseSlots && (
          <>
            {triggerChild}
            <Dialog.Panel>
              {headingChild}
              {contentChild}
              {actionsChild}
            </Dialog.Panel>
          </>
        )}
      </DialogRoot>
    </DialogProvider>
  );
}

/**
 * Trigger Props
 */
export interface TriggerProps {
  /**
   * Used to trigger the dialog to open.  An onClick event will be added dynamically to the component if it supports it.
   * Will throw an error at runtime if the component does not receive support and onClick event handler.
   */
  children: React.ReactElement;
}

/**
 * Dialog.Trigger is used as a convenience for consumers to wrap the component that will trigger the dialog to open.
 * Please note that the child component of the Dialog.Trigger should not implement an onClick event.
 *
 * @note Must be rendered as a direct child of `Dialog`
 */
Dialog.Trigger = function Trigger(props: TriggerProps) {
  return (
    <DialogPrimitive.Trigger {...props} asChild>
      <TriggerAdapter>{props.children}</TriggerAdapter>
    </DialogPrimitive.Trigger>
  );
};

const TriggerAdapter = React.forwardRef<
  HTMLButtonElement,
  { children: React.ReactElement; onClick?: (event: unknown) => void }
>(function TriggerAdapter({ children, onClick }, ref) {
  const child = React.Children.only(children);
  return (
    <child.type
      {...child.props}
      ref={ref}
      onClick={() => {
        onClick?.({ target: {} });
        child.props.onClick?.();
      }}
    />
  );
});

Object.assign(Dialog.Trigger, { [symbolMap.Trigger]: true });

/**
 * Panel properties
 */
export interface PanelProps {
  children: React.ReactNode;
}

/**
 * Dialog.Panel component is a container for the dialog Dialog.Header Dialog.Content and Dialog.Actions
 *
 * @deprecated It is no longer necessary to use Dialog.Panel and it will be removed in the future
 */
Dialog.Panel = function Panel({ children }: PanelProps) {
  const { borderRadiuses, colors, elevations, zIndex } = useFullTheme();
  const responsive = useResponsive();
  const dialogContext = useDialogContext();

  if (!dialogContext) {
    throw new Error('Dialog.Panel can only be used within Dialog');
  }

  // maxWidth's full-width option is blank because the calcWidth will make
  // this full width adjustment by applying 100vw to the minWidth instead of breakpoints.
  const width = { md: '600px', lg: '800px', xl: '1200px', 'full-width': '', 'full-screen': '' }[
    dialogContext.size
  ];

  const calcResponsiveMaxHeight = (margin: number) => `calc(100vh - ${margin * 2}px)`;

  const calcResponsiveWidth = (breakpoint: number, margin: number, size: DialogSize) =>
    size === 'full-width' || size === 'full-screen' || breakpoint === 0
      ? `calc(100vw  - ${margin * 2}px)`
      : `calc(${breakpoint}px  - ${margin * 2}px)`;

  const breakpointsWidths = {
    xs: calcResponsiveWidth(breakpoints.xs, 16, dialogContext.size),
    sm: calcResponsiveWidth(breakpoints.sm, 24, dialogContext.size),
    md: calcResponsiveWidth(breakpoints.md, 32, dialogContext.size),
    lg: calcResponsiveWidth(breakpoints.lg, 40, dialogContext.size),
    xl: calcResponsiveWidth(breakpoints.xl, 48, dialogContext.size),
  };

  const breakpointsHeight = {
    xs: calcResponsiveMaxHeight(16),
    sm: calcResponsiveMaxHeight(24),
    md: calcResponsiveMaxHeight(32),
    lg: calcResponsiveMaxHeight(40),
    xl: calcResponsiveMaxHeight(48),
  };

  const Container = dialogContext.portal ? DialogPrimitive.Portal : React.Fragment;
  if (!dialogContext.portal) {
    console.warn('Dialog.Panel is deprecated and no longer necessary.  Please remove its use');
  }

  return (
    <Container>
      <DialogPrimitive.Overlay
        css={{
          // ensures overlay is positioned correctly
          position: 'fixed',
          zIndex: zIndex.dialog.overlay,
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundColor: 'rgba(0,0,0,0.22)',
          // If aria-hidden is true, that means radix set it to true because a
          // nested dialog was opened.  If a nested dialog is open, we want to
          // hide this parent dialog visually as-well as semantically
          [`&[aria-hidden="true"]`]: {
            visibility: 'hidden',
          },
        }}
      >
        <DialogPrimitive.Content
          css={[
            responsive.css(
              dialogContext.size === 'full-screen'
                ? {
                    height: breakpointsHeight,
                  }
                : {
                    maxHeight: breakpointsHeight,
                  },
            ),
            dialogContext.size === 'full-width' || dialogContext.size === 'full-screen'
              ? responsive.css({
                  width: breakpointsWidths,
                })
              : responsive.css({
                  maxWidth: breakpointsWidths,
                }),
            dialogContext.size !== 'full-width' && dialogContext.size !== 'full-screen'
              ? { width: width }
              : {},
            {
              zIndex: dialogContext.isOpen ? zIndex.dialog.content : undefined,
              position: 'fixed',
              minHeight: '256px',
              borderRadius: borderRadiuses.container,
              backgroundColor: colors.bgRegular,
              boxShadow: elevations.z3,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              flexDirection: 'column',
            },
          ]}
          // When we click on the overlay we need to prevent the Dialog from closing in modal mode
          // TODO:  Investigate why this behavior is happening but no issue related in Radix GitHub.
          onPointerDownOutside={event => event.preventDefault()}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Overlay>
    </Container>
  );
};

/**
 * Heading section props for the Dialog
 */
export interface HeadingProps {
  /** Text in the header of the dialog */
  children: React.ReactNode;

  /** If true, the close button will be disabled */
  disabled?: boolean;

  /**
   * Optional close button to over-ride the default one that shows in top-right
   * of dialog
   */
  closeButton?: React.ReactNode;
}

/**
 * Renders the top heading of a dialog including a close button
 *
 * @note Must be rendered as a direct child of `Dialog`
 */
Dialog.Heading = function Heading({ children, closeButton, disabled = false }: HeadingProps) {
  const { spacing } = useFullTheme();
  const Title = DialogPrimitive.Title;
  const dialogContext = useDialogContext();

  if (!dialogContext) {
    throw new Error('Dialog.Heading can only be used within Dialog');
  }
  return (
    <div
      css={{
        boxShadow:
          dialogContext.scrollPosition === 'bottom' || dialogContext.scrollPosition === 'middle'
            ? '0px 2px 4px rgba(59, 65, 83, 0.12)'
            : 'none',
        display: 'flex',
        justifyContent: 'space-between',
        zIndex: 1,
      }}
    >
      <Title
        css={{
          alignContent: 'center',
          margin: 0,
          minHeight: spacing[16],
        }}
      >
        {/* Title parent component is H2 */}
        <div
          css={{
            alignSelf: 'center',
            marginBottom: spacing[4],
            marginLeft: spacing[6],
            marginTop: spacing[4],
          }}
        >
          <InternalText variant="titleM">{children}</InternalText>
        </div>
      </Title>

      <div css={{ marginTop: spacing[2], marginRight: spacing[2] }}>
        {closeButton || (
          <Button
            aria-label="Close"
            disabled={disabled}
            emphasis="transparent"
            leadIcon={CloseOutlineIcon}
            onClick={() => dialogContext.onOpenChange(false)}
            size="sm"
          />
        )}
      </div>
    </div>
  );
};

Object.assign(Dialog.Heading, { [symbolMap.Heading]: true });

/**
 * Content section props for the Dialog.  Can be anything React can render (forms, text).
 */
export interface ContentProps
  extends Pick<ScrollContainerProps, 'onScroll' | 'scrollThresholdPercentage'> {
  children: React.ReactNode;
  pt?: 0;
}

/**
 * Content section of the Dialog
 *
 * @note Must be rendered as a direct child of `Dialog`
 */
Dialog.Content = function Content({
  children,
  pt,
  onScroll,
  scrollThresholdPercentage,
}: ContentProps) {
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const { spacing, colors, borderRadiuses } = useFullTheme();
  const dialogContext = useDialogContext();
  const topOfContent = 0;
  if (!dialogContext) {
    throw new Error('Dialog.Content can only be used within Dialog');
  }

  const isScrolledBottom = (contents: HTMLDivElement | null) => {
    return (
      contents &&
      contents.offsetHeight &&
      contents.offsetHeight + contents.scrollTop >= contents.scrollHeight
    );
  };

  useEffect(() => {
    const contentsCurrent = contentScrollRef.current;
    const onScroll = () => {
      if (contentScrollRef) {
        if (contentsCurrent?.scrollTop === topOfContent && dialogContext.scrollPosition !== 'top') {
          dialogContext.setScrollPosition('top');
        } else if (isScrolledBottom(contentsCurrent) && dialogContext.scrollPosition !== 'bottom') {
          dialogContext.setScrollPosition('bottom');
        } else {
          if (dialogContext.scrollPosition !== 'middle') {
            dialogContext.setScrollPosition('middle');
          }
        }
      }
    };

    if (contentsCurrent) {
      contentScrollRef.current.addEventListener('scroll', onScroll);
    }

    return () => contentsCurrent?.removeEventListener('scroll', onScroll);
  }, [dialogContext]);

  const onScrollHandler = useOnScrollCallback({
    onScroll,
    ref: contentScrollRef,
    scrollThresholdPercentage,
  });

  return (
    <div
      ref={contentScrollRef}
      css={{
        flex: 1,
        // adding 20px to the child div to compensate for the scroll margin
        marginRight: spacing[1],
        marginLeft: 0,
        overflowY: 'auto',
        '::-webkit-scrollbar': {
          backgroundColor: 'transparent',
          width: spacing[2],
        },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: colors.borderRegularHover,
          borderRadius: borderRadiuses.container,
          marginRight: spacing[1],
        },
      }}
      onScroll={onScrollHandler}
    >
      <div
        css={{
          paddingTop: pt === 0 ? 0 : spacing[6],
          paddingLeft: spacing[6],
          // adjustment for the parent scroll margin placement
          paddingRight: spacing[5],
          paddingBottom: spacing[6],
          height: dialogContext.size === 'full-screen' ? '100%' : undefined,
        }}
      >
        {children}
      </div>
    </div>
  );
};

Object.assign(Dialog.Content, { [symbolMap.Content]: true });

export interface ActionsProps {
  /** The content of the footer. */
  children: React.ReactNode;
}

/**
 * Actions component section of the Dialog. This should be used as the Dialog's footer where the
 * relevant CTAs are placed.
 *
 * @note Must be rendered as a direct child of `Dialog`
 */
Dialog.Actions = function Actions({ children }: ActionsProps) {
  const { spacing } = useFullTheme();
  const dialogContext = useDialogContext();
  if (!dialogContext) {
    throw new Error('Dialog.Actions can only be used within Dialog');
  }

  return (
    <div
      css={{
        display: 'flex',
        boxShadow:
          dialogContext.scrollPosition === 'top' || dialogContext.scrollPosition === 'middle'
            ? '0px -2px 4px rgba(59, 65, 83, 0.12)'
            : 'none',
        zIndex: 1,
      }}
    >
      <div
        css={{
          flexGrow: 1,
          display: 'inline-flex',
          justifyContent: 'space-between',
          margin: spacing[2],
          gap: spacing[2],
        }}
      >
        {children}
      </div>
    </div>
  );
};

Object.assign(Dialog.Actions, { [symbolMap.Actions]: true });

/**
 * Convenience component to be used by consumers for closing/cancelling a Dialog
 */
Dialog.CloseButton = function CloseButton({
  'aria-label': ariaLabel,
  children = 'Cancel',
  disabled,
  emphasis,
  hideAbove,
  hideBelow,
  id,
  intent,
  leadIcon,
  loading,
  size = 'md',
  trailIcon,
  type,
  width = 'hug',
}: Pick<
  ButtonProps,
  | 'aria-label'
  | 'children'
  | 'disabled'
  | 'emphasis'
  | 'hideAbove'
  | 'hideBelow'
  | 'id'
  | 'intent'
  | 'leadIcon'
  | 'loading'
  | 'size'
  | 'trailIcon'
  | 'type'
  | 'width'
>) {
  const dialogContext = useDialogContext();
  if (!dialogContext) {
    throw new Error('Dialog.CloseButton can only be used within Dialog');
  }

  return (
    <Button
      aria-label={ariaLabel}
      disabled={disabled}
      emphasis={emphasis ?? 'highlighted'}
      hideAbove={hideAbove}
      hideBelow={hideBelow}
      id={id}
      intent={intent ?? 'secondary'}
      leadIcon={leadIcon}
      loading={loading}
      onClick={() => dialogContext.onOpenChange(false)}
      size={size}
      trailIcon={trailIcon}
      type={type}
      width={width}
    >
      {children}
    </Button>
  );
};
